import { MAX_CERTIFICATE_PAGE_BYTES } from '@felix/domain';
import {
  type CeAuthorization,
  type CertificateDetail,
  type CertificateList,
  type CertificatePage,
  type CertificatePrinter,
  type ClassConfirmation,
  type ClassDueRow,
  type ClassFinding,
  type ClassItemImportReport,
  type ClassItemList,
  downloadFile,
  type EquipmentChoice,
  type EquipmentTree,
  type OfficeCertificateActions,
  type OfficeUserItem,
  type OnBoardIssuance,
  type PersonChoice,
  type RoleItem,
  type RoleList,
  type UserList,
  type VesselCertificateSource,
  type VesselCertificateType,
  type VesselSurvey,
} from '@felix/ui';
import { ApiError, api, officeApi, pmsReportFile } from './api.js';
import { tusUpload } from './tus-upload.js';

/**
 * Taranmış sertifika sayfası yüklemesinin amacı: sunucu sınırı (25 MB) ve sayfaya eklemede amaç
 * denetimi buna göre (`apps/server/src/office/uploads.ts` `CERTIFICATE_PAGE_UPLOAD_PURPOSE`; K34).
 */
const CERTIFICATE_PAGE_UPLOAD_PURPOSE = 'certificate_page';
const CSV_TYPE = 'text/csv';
const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const id = encodeURIComponent;

/** Listelerin zarfı (`{ items, can? }`): ekran yalnız satırları okur. */
async function items<T>(path: string): Promise<T[]> {
  return (await api<{ items: T[] }>(path)).items;
}

/** Ofis dosya ucu: aynı köken, oturum çerezi, `Range` destekli (ADR 0007; K34). */
export function certificatePageUrl(pageId: string): string {
  return `/api/office/certificate-pages/${id(pageId)}/file`;
}

/**
 * Kalem listesi dosyasının türü (plan Task 25): `.csv` ya da `text/csv` CSV, diğerleri XLSX.
 * Sunucu içeriği yeniden okur; tür yalnız gövde ayrıştırıcısını seçer.
 */
export function classItemFileType(file: Blob & { name: string }): string {
  return file.type === CSV_TYPE || /\.csv$/i.test(file.name) ? CSV_TYPE : XLSX_TYPE;
}

/** Klas bulgusunun ekipman seçicisi: ağacın düğümleri kod sırasıyla düz liste. */
export function equipmentChoices(tree: EquipmentTree): EquipmentChoice[] {
  return tree.nodes
    .map((node) => ({ id: node.id, code: node.code, name: node.name }))
    .sort((a, b) => a.code.localeCompare(b.code, 'en', { numeric: true }));
}

const current = (row: { validFrom: string; validTo: string | null }, today: string) =>
  row.validFrom <= today && (row.validTo === null || row.validTo >= today);

/**
 * Yetki beyanının kişisi (K31): şirketin etkin kullanıcıları; başmühendis gemide henüz görev
 * almamış olabilir. Görev bu gemideki bugünkü rolden, yoksa bugünkü şirket rolünden; ada göre.
 */
export function personChoices(
  users: readonly Pick<
    OfficeUserItem,
    'id' | 'fullName' | 'active' | 'companyRoles' | 'vesselAssignments'
  >[],
  roles: readonly Pick<RoleItem, 'id' | 'name'>[],
  vesselId: string,
  today: string,
): PersonChoice[] {
  const roleName = new Map(roles.map((role) => [role.id, role.name]));
  return users
    .filter((user) => user.active)
    .map((user) => {
      const onVessel = user.vesselAssignments.find(
        (row) => row.vesselId === vesselId && current(row, today),
      );
      const company = user.companyRoles.find((row) => current(row, today));
      const roleId = onVessel?.roleId ?? company?.roleId;
      return {
        id: user.id,
        name: user.fullName,
        role: roleId === undefined ? null : (roleName.get(roleId) ?? null),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
}

/** Ham gövdeli istek (kalem listesi dosyası); hata `ApiError`. */
async function rawPost<T>(path: string, body: Blob, type: string): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': type },
      body,
    });
  } catch {
    throw new ApiError('NETWORK', 0);
  }
  const data = (await response.json().catch(() => ({}))) as { error?: string; detail?: unknown };
  if (!response.ok) {
    throw new ApiError(
      data.error ?? 'INTERNAL',
      response.status,
      typeof data.detail === 'string' ? data.detail : undefined,
      data,
    );
  }
  return data as T;
}

/** Tarayıcı indirmesi: sunucu `Content-Disposition: attachment` verir; dosya belleğe okunmaz. */
function browserDownload(pageId: string, name: string): void {
  const link = document.createElement('a');
  link.href = certificatePageUrl(pageId);
  link.download = name;
  link.rel = 'noopener';
  document.body.append(link);
  link.click();
  link.remove();
}

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Ofisin formları (plan M7 Task 27b; ADR 0029 K28–K31, K34): `apps/server/src/office/
 * vessel-certificates.ts` ve `class-items.ts`. Vize, teyit ve gemide düzenlenen sertifika
 * geminindir: ofiste yazma yok (sunucu 409 `SHIP_IS_WRITER`).
 */
export function officeCertificateActions(owner?: string): OfficeCertificateActions {
  const remove = async (path: string, rowVersion: number) => {
    await api(`${path}?rowVersion=${rowVersion}`, { method: 'DELETE' });
  };
  return {
    create: (vesselId, input) =>
      api<CertificateDetail>(`/office/vessels/${id(vesselId)}/vessel-certificates`, {
        method: 'POST',
        body: input,
      }),
    update: (certificateId, rowVersion, patch) =>
      api<CertificateDetail>(`/office/vessel-certificates/${id(certificateId)}`, {
        method: 'PATCH',
        body: { rowVersion, ...patch },
      }),
    remove: (certificateId, rowVersion) =>
      remove(`/office/vessel-certificates/${id(certificateId)}`, rowVersion),
    acceptIssuance: (vesselId, issuanceId) =>
      api<CertificateDetail>(
        `/office/vessels/${id(vesselId)}/vessel-certificates/issued-on-board/${id(issuanceId)}/accept`,
        { method: 'POST', body: {} },
      ),
    addRemark: (certificateId, rowVersion, text) =>
      api<CertificateDetail>(`/office/vessel-certificates/${id(certificateId)}/remarks`, {
        method: 'POST',
        body: { rowVersion, text },
      }),
    uploadPage: async (certificateId, file, onProgress) => {
      // Sunucu da denetler; büyük dosya hiç gönderilmez
      if (file.size <= 0 || file.size > MAX_CERTIFICATE_PAGE_BYTES) {
        throw new ApiError('FILE_TOO_LARGE', 413);
      }
      const uploadId = await tusUpload(file, {
        purpose: CERTIFICATE_PAGE_UPLOAD_PURPOSE,
        ...(owner ? { owner } : {}),
        onProgress: onProgress ?? (() => undefined),
      });
      return api<CertificatePage>(`/office/vessel-certificates/${id(certificateId)}/pages`, {
        method: 'POST',
        body: { uploadId, fileName: file.name },
      });
    },
    movePage: async (pageId, rowVersion, sortOrder) => {
      await api(`/office/certificate-pages/${id(pageId)}`, {
        method: 'PATCH',
        body: { rowVersion, sortOrder },
      });
    },
    removePage: (pageId, rowVersion) =>
      remove(`/office/certificate-pages/${id(pageId)}`, rowVersion),
    createSurvey: async (vesselId, input) => {
      await api(`/office/vessels/${id(vesselId)}/surveys`, { method: 'POST', body: input });
    },
    updateSurvey: async (surveyId, rowVersion, patch) => {
      await api(`/office/vessel-surveys/${id(surveyId)}`, {
        method: 'PATCH',
        body: { rowVersion, ...patch },
      });
    },
    removeSurvey: (surveyId, rowVersion) =>
      remove(`/office/vessel-surveys/${id(surveyId)}`, rowVersion),
    createFinding: async (vesselId, input) => {
      await api(`/office/vessels/${id(vesselId)}/class-findings`, { method: 'POST', body: input });
    },
    updateFinding: async (findingId, rowVersion, patch) => {
      await api(`/office/class-findings/${id(findingId)}`, {
        method: 'PATCH',
        body: { rowVersion, ...patch },
      });
    },
    removeFinding: (findingId, rowVersion) =>
      remove(`/office/class-findings/${id(findingId)}`, rowVersion),
    createAuthorization: async (vesselId, input) => {
      await api(`/office/vessels/${id(vesselId)}/ce-authorizations`, {
        method: 'POST',
        body: input,
      });
    },
    updateAuthorization: async (authorizationId, rowVersion, patch) => {
      await api(`/office/ce-authorizations/${id(authorizationId)}`, {
        method: 'PATCH',
        body: { rowVersion, ...patch },
      });
    },
    removeAuthorization: (authorizationId, rowVersion) =>
      remove(`/office/ce-authorizations/${id(authorizationId)}`, rowVersion),
    importClassItems: (vesselId, file, apply) =>
      rawPost<ClassItemImportReport>(
        `/office/vessels/${id(vesselId)}/class-items/import${apply ? '?apply=true' : ''}`,
        file,
        classItemFileType(file),
      ),
    equipment: async (vesselId) => equipmentChoices(await officeApi.equipmentTree(vesselId)),
    // Şirketin kullanıcı listesi (`/office/users`, "kullanıcılar / görüntüle"): kişi gemide görev
    // almamış olabilir (K31); `/office/crew` yalnız gemi ataması olanları verir
    people: async (vesselId) => {
      const [users, roles] = await Promise.all([
        api<UserList>('/office/users'),
        api<RoleList>('/office/roles').catch(() => ({ roles: [] as RoleItem[] })),
      ]);
      return personChoices(users.users, roles.roles, vesselId, today());
    },
  };
}

/**
 * Ofiste Vessel Certificates (plan M7 Task 27a, 27b; `documents-source.ts` kalıbı): ofis API'si.
 * Sayfa önizleyicisi dosyayı `Range` ile okur; yazdırma tarayıcının yazdırma penceresidir.
 */
export function officeVesselCertificateSource(
  printer: CertificatePrinter,
  owner?: string,
): VesselCertificateSource {
  return {
    scope: 'web',
    vessels: () => officeApi.vessels(),
    particulars: (vesselId) => officeApi.particulars(vesselId),
    types: () => items<VesselCertificateType>('/office/vessel-certificate-types'),
    list: (vesselId) => api<CertificateList>(`/office/vessels/${id(vesselId)}/vessel-certificates`),
    detail: (certificateId) =>
      api<CertificateDetail>(`/office/vessel-certificates/${id(certificateId)}`),
    surveys: (vesselId) => items<VesselSurvey>(`/office/vessels/${id(vesselId)}/surveys`),
    findings: (vesselId) => items<ClassFinding>(`/office/vessels/${id(vesselId)}/class-findings`),
    authorizations: (vesselId) =>
      items<CeAuthorization>(`/office/vessels/${id(vesselId)}/ce-authorizations`),
    classItems: (vesselId) => api<ClassItemList>(`/office/vessels/${id(vesselId)}/class-items`),
    classDueList: (vesselId) =>
      items<ClassDueRow>(`/office/vessels/${id(vesselId)}/class-due-list`),
    confirmations: (vesselId) =>
      items<ClassConfirmation>(`/office/vessels/${id(vesselId)}/class-confirmations`),
    issuances: (vesselId) =>
      items<OnBoardIssuance>(`/office/vessels/${id(vesselId)}/vessel-certificates/issued-on-board`),
    // Ofis ucu tek: önizleyici `Range` ile okur, indirme bağlantının `download` adıyla
    pageUrl: (pageId) => certificatePageUrl(pageId),
    downloadPage: async (page) => browserDownload(page.id, page.fileName),
    print: async () => {
      window.print();
    },
    printer,
    // M7e: Vessel Certificates raporu; Excel ve CSV sunucuda üretilir, tarayıcı indirir
    exportReport: async (vesselId, options) => {
      const file = await pmsReportFile(vesselId, 'vessel-certificates', {
        format: options.format,
        lang: options.lang,
        query: options.query,
      });
      downloadFile(file.fileName, file.bytes, file.type);
    },
    auditTrail: (vesselId, filter) => officeApi.auditTrail(vesselId, filter),
    office: officeCertificateActions(owner),
  };
}
